import { useMemo, useState } from "react";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import { vocabularyOf } from "src/modules/engagement/components/phases/emailSms/touchCopy";
import InfoTip from "@/shared/components/infoTip/InfoTip";
import TouchPreview from "src/modules/engagement/components/phases/emailSms/TouchPreview";
import {
  anchorName,
  type Sequence,
} from "src/modules/engagement/components/phases/emailSms/emailSmsTypes";
import type { SenderIdentity } from "@services/sequence/sequenceService";
import FilterMenu from "./FilterMenu";
import { catalogMessages } from "./catalogData";

type ChannelFilter = "all" | "email" | "sms";

const STATUS_OPTIONS = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

type Props = {
  onOpen: (touchId: string) => void;
  sequences: Sequence[];
  /** The stages a playbook can be filed under, from the Sequence Stage tag group. */
  stageOptions: string[];
  /** Who a catalog preview renders as. Fictional -- a catalog message has no campaign. */
  preview: SenderIdentity | null;
};

export function MessagesTab({
  onOpen,
  sequences,
  stageOptions: STAGES,
  preview,
}: Props) {
  const [query, setQuery] = useState("");
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [stages, setStages] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { tokens: adTokens } = useAdTokens();
  // Same vocabulary the editor uses, so a token reads the same in the list's preview as it
  // does once the message is open. Without it the preview called every registered token the
  // samples do not cover "unknown" rather than "no value".
  const vocabulary = useMemo(() => vocabularyOf(adTokens), [adTokens]);

  const all = useMemo(() => catalogMessages(sequences), [sequences]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all.filter((row) => {
      if (channel !== "all" && row.touch.ch !== channel) return false;
      if (stages.length && !stages.includes(row.sequence.stage)) return false;
      if (statuses.length) {
        const status = row.touch.pub ? "published" : "draft";
        if (!statuses.includes(status)) return false;
      }
      if (!needle) return true;
      return [row.touch.title, row.touch.subject ?? "", row.touch.text ?? "", row.sequence.name]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [all, query, channel, stages, statuses]);

  const selected = rows.find((r) => r.touch.id === selectedId) ?? rows[0] ?? null;
  const anyFilter = query !== "" || channel !== "all" || stages.length > 0 || statuses.length > 0;

  const clearAll = () => {
    setQuery("");
    setChannel("all");
    setStages([]);
    setStatuses([]);
  };

  return (
    <>
      <div className="cat-filters">
        <input
          type="search"
          className="cat-search"
          value={query}
          placeholder="Search messages, subjects or sequences"
          aria-label="Search messages"
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="btn-group btn-group-sm" role="group" aria-label="Channel">
          {(["all", "email", "sms"] as ChannelFilter[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn btn-sm ${channel === option ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setChannel(option)}
            >
              {option === "all" ? "All" : option === "email" ? "Email" : "SMS"}
            </button>
          ))}
        </div>
        <FilterMenu
          allLabel="All stages"
          options={STAGES.map((s) => ({ value: s, label: s }))}
          selected={stages}
          onChange={setStages}
        />
        <FilterMenu
          allLabel="All statuses"
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setStatuses}
        />
        {anyFilter && (
          <button type="button" className="esms__link" onClick={clearAll}>
            Clear filters
          </button>
        )}
      </div>

      <div className="cat-split">
        <div className="cat-table">
          <div className="cat-table__head">
            <span className="cat-table__title">Messages</span>
            <span className="esms-count">{rows.length}</span>
            <InfoTip label="Messages" text="Email and SMS content, tagged to a funnel stage." />
          </div>

          {rows.length === 0 ? (
            <div className="cat-empty">
              <div className="cat-empty__title">No messages match these filters</div>
              <div className="cat-empty__body">
                Try a different search, or clear the filters to see every message.
              </div>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={clearAll}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="cat-table__scroll">
              <div className="cat-table__inner">
                <div className="cat-row cat-row--head">
                  <span>Message</span>
                  <span>Channel</span>
                  <span>Funnel stage</span>
                  <span>Sequence</span>
                  <span>Touch</span>
                  <span>Default trigger</span>
                  <span>Status</span>
                  <span>Last updated</span>
                </div>
                {rows.map((row) => (
                  <button
                    type="button"
                    key={row.touch.id}
                    className={`cat-row${selected?.touch.id === row.touch.id ? " cat-row--on" : ""}`}
                    /*
                        One click selects and shows the preview; a second opens the message,
                        as does a double click on a row that was not selected. The row was
                        select-only, so the preview's Open button was the single way in and
                        a click on the row a reader expected to open did nothing visible.
                    */
                    onClick={() => {
                      if (selected?.touch.id === row.touch.id) onOpen(row.touch.id);
                      else setSelectedId(row.touch.id);
                    }}
                    onDoubleClick={() => onOpen(row.touch.id)}
                  >
                    <span className="cat-row__name-cell">
                      <span className="cat-row__name">{row.touch.title}</span>
                      <span className="cat-row__sub">
                        {row.touch.ch === "email" ? row.touch.subject : row.touch.text}
                      </span>
                    </span>
                    <span>
                      <span className={`touch-ch touch-ch--${row.touch.ch}`}>
                        {row.touch.ch === "email" ? "Email" : "SMS"}
                      </span>
                    </span>
                    <span>{row.sequence.stage}</span>
                    <span className="cat-row__ellipsis">{row.sequence.name}</span>
                    <span className="cat-row__nums">{row.touchN}</span>
                    <span className="cat-row__nums">{row.trigger}</span>
                    <span>
                      <span
                        className={`touch-pill touch-pill--${row.touch.pub ? "on" : "blocked"}`}
                      >
                        {row.touch.pub ? "Published" : "Draft"}
                      </span>
                    </span>
                    <span className="cat-row__updated">{row.touch.updated ?? "—"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {selected && (
          <aside className="cat-detail" aria-label="Message detail">
            <div className="cat-detail__head">
              <span className="cat-detail__name">{selected.touch.title}</span>
              <span className={`touch-pill touch-pill--${selected.touch.pub ? "on" : "blocked"}`}>
                {selected.touch.pub ? "Published" : "Draft"}
              </span>
            </div>
            <div className="cat-detail__body">
              <div className="cat-detail__actions">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onOpen(selected.touch.id)}
                >
                  Open
                </button>
                <button type="button" className="btn btn-primary btn-sm" disabled>
                  Edit design
                </button>
              </div>

              <div
                role="button"
                tabIndex={0}
                aria-label="Open in the editor"
                className="cat-detail__preview"
                onClick={() => onOpen(selected.touch.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onOpen(selected.touch.id);
                }}
              >
                <div className="cat-detail__preview-inner">
                  <TouchPreview
                    touch={selected.touch}
                    values={preview?.sample_values ?? {}}
                    vocabulary={vocabulary}
                    fromName={preview?.from_name ?? ""}
                    fromAddress={preview?.from_address ?? ""}
                    smsSender={preview?.sms_sender ?? ""}
                    footerAddress={preview?.footer_address ?? ""}
                    showScaleLabel={false}
                  />
                </div>
              </div>

              <dl className="cat-detail__meta">
                <dt>Sequence</dt>
                <dd>{selected.sequence.name}</dd>
                <dt>Funnel stage</dt>
                <dd>{selected.sequence.stage}</dd>
                <dt>Target action</dt>
                <dd>{selected.sequence.target}</dd>
                <dt>Default trigger</dt>
                <dd>{selected.trigger}</dd>
                <dt>Key date</dt>
                <dd>{anchorName(selected.touch.anchor)}</dd>
                <dt>Send hour</dt>
                <dd>{selected.touch.hr}:00 recipient local</dd>
                <dt>Version</dt>
                <dd>v{selected.touch.ver}</dd>
              </dl>
            </div>
          </aside>
        )}
      </div>
    </>
  );
}

export default MessagesTab;
